import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { captureException } from "@/lib/analytics";

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  section?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error(`[ErrorBoundary${this.props.section ? `:${this.props.section}` : ""}]`, error, errorInfo);
    captureException(error, {
      section: this.props.section || 'unknown',
      componentStack: errorInfo.componentStack,
    });
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <Card className="border-destructive/30 bg-destructive/5" data-testid="error-boundary-fallback">
          <CardContent className="flex flex-col items-center justify-center py-12 gap-4">
            <AlertTriangle className="w-10 h-10 text-destructive" />
            <div className="text-center space-y-1">
              <h3 className="font-semibold text-lg">
                {this.props.section ? `${this.props.section} encountered an error` : "Something went wrong"}
              </h3>
              <p className="text-sm text-muted-foreground max-w-md">
                {this.state.error?.message || "An unexpected error occurred. Please try refreshing."}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => this.setState({ hasError: false, error: null })}
              data-testid="button-error-retry"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Try Again
            </Button>
          </CardContent>
        </Card>
      );
    }

    return this.props.children;
  }
}

export function GlobalErrorFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background" data-testid="global-error-fallback">
      <div className="text-center space-y-4 max-w-md px-6">
        <AlertTriangle className="w-16 h-16 text-destructive mx-auto" />
        <h1 className="text-2xl font-bold">Something went wrong</h1>
        <p className="text-muted-foreground">
          The application encountered an unexpected error. Please reload the page to continue.
        </p>
        <Button onClick={() => window.location.reload()} data-testid="button-reload-app">
          <RefreshCw className="w-4 h-4 mr-2" />
          Reload Page
        </Button>
      </div>
    </div>
  );
}
